import React, { useEffect, useState } from "react";
import { Button } from "../ui/Button";
import { Plus, Save, Trash2, SquarePen, CloudUpload } from "lucide-react";
import { FormControl, InputLabel, MenuItem, Select, IconButton, InputAdornment } from "@mui/material";
import { useAccountMappingHeads, useCreateAccountMapping, useUpdateAccountMapping, useAccountMapping, useDeleteAccountMapping, useUpdateAccountMappingName, usePublishAccountMappings } from "@/hooks/use-account-mapping";
import { CreateEditAccountMappingDialog } from "./CreateEditAccountMappingDialog";
import EditAccountMapping from "./EditAccountMapping";
import { LoadingSpinner } from "../LoadingSpinner";
import Card from "../ui/Card";
import { AccountMapping } from "@/types/account-mapping-api";
import { ConfirmDialog } from "../common/ConfirmDialog";

interface AccountMappingSubModuleProps {
  clientId: string;
}

const AccountMappingSubModule: React.FC<AccountMappingSubModuleProps> = ({ clientId }) => {
  
  // state
  const [isCreateEditDialogOpen, setIsCreateEditDialogOpen] = useState<boolean>(false);
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState<boolean>(false);
  const [selectedMappingId, setSelectedMappingId] = useState<string | null>(null);
  const [isMappingDirty, setIsMappingDirty] = useState<boolean>(false);
  const [editedMapping, setEditedMapping] = useState<AccountMapping | null>(null);
  const [mappingNameToEdit, setMappingNameToEdit] = useState<string | null>(null);

  useEffect(() => {
    // Any additional setup can go here
  }, []);

  // hooks
  const { accountMappingHeads, isLoadingAccountMappingHeads, accountMappingHeadsError } = useAccountMappingHeads(clientId);
  const { createNewAccountMapping, isCreating, createSuccess, createError, createdMapping } = useCreateAccountMapping(clientId);
  const { updateAccountMapping, isUpdating, updateSuccess, updateError } = useUpdateAccountMapping(editedMapping);
  const { selectedAccountMapping } = useAccountMapping(selectedMappingId!);
  const { deleteMapping, isDeleting, deleteSuccess, deleteError } = useDeleteAccountMapping(selectedMappingId);
  const { updateAccountMappingName, isUpdatingMappingName, updateMappingNameSuccess, updateMappingNameError } = useUpdateAccountMappingName(selectedMappingId);
  const { publishAccountMappings, isPublishing } = usePublishAccountMappings(clientId);

  // Compare original mapping with edited mapping
  useEffect(() => {
    if (selectedAccountMapping && editedMapping) {
      const isDirty = JSON.stringify(selectedAccountMapping) !== JSON.stringify(editedMapping);
      setIsMappingDirty(isDirty);
    } else {
      setIsMappingDirty(false);
    }
  }, [selectedAccountMapping, editedMapping]);

  // Track if mapping has been modified - now receives the edited mapping
  const handleMappingChanged = (mapping: AccountMapping | null) => {
    setEditedMapping(mapping);
  };

  useEffect(() => {
    if (createSuccess && createdMapping) {
      setSelectedMappingId(createdMapping.id);
    }
  }, [createSuccess, createdMapping]);

  // Reset dirty state and edited mapping when switching mappings
  useEffect(() => {
    setIsMappingDirty(false);
    setEditedMapping(null);
  }, [selectedMappingId]);

  // handlers
  const handleCreateEditAccountMapping = async (inEditMode: boolean, name: string) => {
    if (inEditMode && selectedMappingId) {
      updateAccountMappingName(name);
      setMappingNameToEdit(null);
    }
    else {
      setSelectedMappingId(null);
      createNewAccountMapping({
        name: name,
        clientId: clientId,
      });
    }
  };

  const handleSaveMapping = () => {
    updateAccountMapping(editedMapping);
    setIsMappingDirty(false);
  };

  if (isLoadingAccountMappingHeads || accountMappingHeadsError) {
    return (<LoadingSpinner error={accountMappingHeadsError && accountMappingHeadsError.message} />);
  }

  return (
    <div className="py-4">
      <div className="flex flex-row justify-between items-center mb-4">
        <div className="flex flex-row items-center w-1/2">
          <FormControl className="flex-grow" disabled={isCreating || !accountMappingHeads}>
            <InputLabel className="bg-background-default">Välj mappning</InputLabel>
            <Select
              value={selectedMappingId || ""} // Ensure a controlled component
              onChange={(e) => setSelectedMappingId(e.target.value)}
              endAdornment={selectedMappingId && (
                <InputAdornment position="end" className="mr-4">
                  <IconButton
                    title="Ta bort mappning"
                    size="small"
                    disabled={isCreating || isUpdating || isLoadingAccountMappingHeads}
                    onClick={() => {setIsConfirmDeleteDialogOpen(true)}}
                  >
                    <Trash2 className="h-4 w-4" />
                  </IconButton>
                  <IconButton
                    title="Redigera mappningsnamn"
                    size="small"
                    disabled={isCreating || isUpdating || isLoadingAccountMappingHeads}
                    onClick={() => { setMappingNameToEdit(accountMappingHeads.find(x => x.id == selectedMappingId).name); setIsCreateEditDialogOpen(true); }}
                  >
                    <SquarePen className="h-4 w-4" />
                  </IconButton>
                </InputAdornment>
              )}
            >
              {accountMappingHeads && accountMappingHeads.map((head) => (
                <MenuItem key={head.id} value={head.id}>
                  {head.name}
                </MenuItem>
              ))}
              {accountMappingHeads && accountMappingHeads.length === 0 &&
                (<MenuItem>
                  Ingen mappning funnen
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </div>
        <div className="flex flex-row gap-2">
          <Button
            onClick={() => { publishAccountMappings()}}
            startIcon={<CloudUpload className="h-4 w-4" />}
            disabled={!accountMappingHeads?.length || isCreating || isUpdating || isLoadingAccountMappingHeads|| isPublishing}
            variant={"outlined"}
          >
            Publicera
          </Button>
          <Button
            variant="outlined"
            startIcon={<Plus className="h-4 w-4" />}
            disabled={isCreating || isLoadingAccountMappingHeads}
            onClick={() => setIsCreateEditDialogOpen(true)}
            >
            Skapa ny mappning
          </Button>
          <Button
            startIcon={<Save className="h-4 w-4" />}
            disabled={isCreating || isUpdating || isLoadingAccountMappingHeads || !selectedMappingId || !isMappingDirty}
            onClick={handleSaveMapping}
            >
            Spara mappning
          </Button>
        </div>
      </div>
      <CreateEditAccountMappingDialog
        open={isCreateEditDialogOpen}
        nameToEdit={mappingNameToEdit}
        onOpenChange={() => { setIsCreateEditDialogOpen(false); setMappingNameToEdit(null); }}
        onSubmitAccountMapping={handleCreateEditAccountMapping}
      />
      {(isCreating || isPublishing || isUpdating) && <LoadingSpinner />}
      {!isCreating && !isPublishing && !isUpdating && !selectedMappingId && <Card hoverable={false} className="w-full border border-palette-dark-gray rounded-lg flex flex-col items-center justify-center gap-2 p-8">
        <span className="text-lg font-semibold">Skapa eller välj en mappning</span>
        <span className="text-sm text-gray-500">
            Starta genom att skapa en ny mappning eller välj en befintlig.
        </span>
      </Card>}
      {!isCreating && !isPublishing&& !isUpdating && selectedMappingId && <EditAccountMapping 
        clientId={clientId} 
        accountMappingId={selectedMappingId}
        onMappingChanged={handleMappingChanged}
      />}
      {selectedMappingId && <ConfirmDialog
        open={isConfirmDeleteDialogOpen}
        confirmId={selectedMappingId!}
        title="Bekräfta borttagning"
        text={`Är du säker på att du vill ta bort mappning ${accountMappingHeads.find(x => x.id == selectedMappingId)?.name}? Detta kan inte ångras.`}
        onOpenChange={setIsConfirmDeleteDialogOpen}
        onConfirm={(confirmId) => {
          deleteMapping(confirmId);
          setIsConfirmDeleteDialogOpen(false);
          setSelectedMappingId(null);
        }}
      />}
    </div>
  );
};

export default AccountMappingSubModule;